/* eslint-disable no-unused-vars */
import React, {useEffect, useState, useCallback} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {
  purchaseProposalRequestGetAll,
  purchaseProposalRequestUpdate,
} from '../../../redux/slices/purchase.slice'
import {useParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import {toAbsoluteUrl, KTSVG} from '../../../helpers'
import {Modal} from 'react-bootstrap'
import {Loading, InputPrice, KTCardBody, KTCardHeader} from '../../../components'

import {
  useMemoizedValidationSchema,
  useMemoizedFormValues
} from '../../../utils/performanceOptimizations'

const selectPurchaseData = (state) => ({
  getCompany: state.purchase.getCompany,
  postCompany: state.purchase.postCompany,
  loading: state.purchase.loading
})

function PurchaseProposalRequest() {
  const {id} = useParams()
  const intl = useIntl()
  const dispatch = useDispatch()
  const {getCompany, postCompany, loading} = useSelector(selectPurchaseData)
  const [show, setShow] = useState(false)

  useEffect(() => {
    if (id) {
      dispatch(purchaseProposalRequestGetAll({purchaseProposalGuid: id}))
    }
  }, [dispatch, id])

  useEffect(() => {
    if (postCompany) {
      setShow(true)
    }
  }, [postCompany])

  const validSchema = useMemoizedValidationSchema(() => Yup.object().shape({
    Note: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    File: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    TotalPrice: Yup.number().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    TotalPriceWithTax: Yup.number().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
  }), [intl])

  const formValues = useMemoizedFormValues({
    PurchaseProposalGuid: id,
    Note: '',
    File: null,
    PaymentDetail: '',
    LaborTotalPrice: 0,
    ProductTotalPrice: 0,
    TotalPrice: 0,
    TotalPriceWithTax: 0,
    Brand: '',
    TSEFile: null,
    Transportation: '',
    DeliveryTime: '',
  })

  const handleSubmit = useCallback(async (values, {setSubmitting, resetForm}) => {
    setSubmitting(true)
    dispatch(purchaseProposalRequestUpdate(values))
    resetForm()
  }, [dispatch])

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  return (
    <>
      {getCompany && (
        <KTCardBody className='p-0 pt-4'>
          <div className='text-center mb-11'>
            <h1 className='text-dark fw-bolder mb-3'>{getCompany && getCompany.companyName}</h1>
            {getCompany && getCompany.description && (
              <div className='text-gray-500 fw-semibold fs-5'>{getCompany && getCompany.description}</div>
            )}
            {getCompany && getCompany.latestDateProposal && (
              <div className='text-gray-500 fw-semibold fs-5'>
                {intl.formatDate(new Date(getCompany && getCompany.latestDateProposal), {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </div>
            )}
            {getCompany && getCompany.fileUrl && (
              <div className='d-flex align-items-center justify-content-center gap-3'>
                <div className='fw-semibold fs-6 text-gray-400'>
                  {intl.formatMessage({
                    id: 'REQUEST.GET.PURCHASEPROPOSALITEMS.PROPOSALFILEURL',
                  })}
                </div>
                <div className='fw-bold fs-4'>
                  <a
                    href={getCompany && getCompany.fileUrl}
                    target='_blank'
                    rel='noreferrer'
                    className='d-flex align-items-center justify-content-center'
                  >
                    <div className='symbol symbol-25px me-2 text-center'>
                      <span className='symbol-label'>
                        <img
                          src={toAbsoluteUrl('/images/icons/duotune/files/fil021.svg')}
                          className='align-self-center'
                          alt=''
                        />
                      </span>
                    </div>
                  </a>
                </div>
              </div>
            )}
          </div>
        </KTCardBody>
      )}

      <KTCardHeader
        className='ps-0'
        title={intl.formatMessage({id: 'REQUEST.GET.PURCHASEPROPOSALREQUEST.MODAL.TITLE'})}
      />

      <KTCardBody className='p-0'>
        <form className='form' onSubmit={formik.handleSubmit} noValidate>
          <div className='row mt-11'>
            <div className='col-md-12'>
              <div className='row'>
                <div className='col-lg-6'>
                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                      {intl.formatMessage({id: 'REQUEST.GET.PURCHASEPROPOSALREQUESTITEMS.NOTE'})}
                    </label>
                    <div className='col-lg-9 fv-row fv-plugins-icon-container'>
                      <textarea
                        className='form-control form-control-solid mb-3 mb-lg-0'
                        disabled={formik.isSubmitting}
                        rows={2}
                        {...formik.getFieldProps('Note')}
                      />
                      {formik.touched.Note && formik.errors.Note && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.Note}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className='col-lg-6'>
                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                      {intl.formatMessage({
                        id: 'REQUEST.GET.PURCHASEPROPOSALREQUESTITEMS.PROPOSALFILEURL',
                      })}
                    </label>
                    <div className='col-lg-9 fv-row fv-plugins-icon-container'>
                      <label
                        htmlFor='File'
                        className='btn btn-outline btn-outline-dashed btn-outline-dark btn-active-light-dark position-relative overflow-hidden d-flex align-items-center justify-content-center'
                      >
                        <input
                          className='position-absolute z-index-2 opacity-0 w-1px h-1px'
                          type='file'
                          accept='image/*,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.kmz,.kml,.ncz,.dwg,.kmz,.kml,.ncz,.dwg'
                          id={'File'}
                          name={'File'}
                          onChange={(event) =>
                            formik.setFieldValue('File', event.currentTarget.files[0])
                          }
                        />
                        <KTSVG
                          path='/images/icons/duotune/communication/com008.svg'
                          className='svg-icon-3 position-relative'
                        />
                        {formik.values.File
                          ? formik.values.File.name
                          : intl.formatMessage({id: 'REQUEST.CREATE.FILE'})}
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className='row'>
                <div className='col-lg-6'>
                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2'>
                      {intl.formatMessage({
                        id: 'REQUEST.GET.PURCHASEPROPOSALREQUESTITEMS.PAYMENTDETAIL',
                      })}
                    </label>
                    <div className='col-lg-9 fv-row fv-plugins-icon-container'>
                      <input
                        type='text'
                        className='form-control form-control-solid mb-3 mb-lg-0'
                        autoComplete='off'
                        disabled={formik.isSubmitting}
                        {...formik.getFieldProps('PaymentDetail')}
                      />
                    </div>
                  </div>
                </div>
                <div className='col-lg-6'>
                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2'>
                      {intl.formatMessage({
                        id: 'REQUEST.GET.PURCHASEPROPOSALREQUESTITEMS.DELIVERYTIME',
                      })}
                    </label>
                    <div className='col-lg-9 fv-row fv-plugins-icon-container'>
                      <input
                        type='text'
                        className='form-control form-control-solid mb-3 mb-lg-0'
                        autoComplete='off'
                        disabled={formik.isSubmitting}
                        {...formik.getFieldProps('DeliveryTime')}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className='row'>
                <div className='col-lg-6'>
                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2'>
                      {intl.formatMessage({
                        id: 'REQUEST.GET.PURCHASEPROPOSALREQUESTITEMS.LABORTOTALPRICE',
                      })}
                    </label>
                    <div className='col-lg-9 fv-row fv-plugins-icon-container'>
                      <InputPrice
                        {...formik.getFieldProps('LaborTotalPrice')}
                        onChange={(value) => formik.setFieldValue('LaborTotalPrice', Number(value))}
                      />
                    </div>
                  </div>
                </div>
                <div className='col-lg-6'>
                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2'>
                      {intl.formatMessage({
                        id: 'REQUEST.GET.PURCHASEPROPOSALREQUESTITEMS.PRODUCTTOTALPRICE',
                      })}
                    </label>
                    <div className='col-lg-9 fv-row fv-plugins-icon-container'>
                      <InputPrice
                        {...formik.getFieldProps('ProductTotalPrice')}
                        onChange={(value) =>
                          formik.setFieldValue('ProductTotalPrice', Number(value))
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className='row'>
                <div className='col-lg-6'>
                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                      {intl.formatMessage({
                        id: 'REQUEST.GET.PURCHASEPROPOSALREQUESTITEMS.TOTALPRICE',
                      })}
                    </label>
                    <div className='col-lg-9 fv-row fv-plugins-icon-container'>
                      <InputPrice
                        {...formik.getFieldProps('TotalPrice')}
                        onChange={(value) => formik.setFieldValue('TotalPrice', Number(value))}
                      />
                      {formik.touched.TotalPrice && formik.errors.TotalPrice && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.TotalPrice}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className='col-lg-6'>
                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                      {intl.formatMessage({
                        id: 'REQUEST.GET.PURCHASEPROPOSALREQUESTITEMS.TOTALPRICEWITHTAX',
                      })}
                    </label>
                    <div className='col-lg-9 fv-row fv-plugins-icon-container'>
                      <InputPrice
                        {...formik.getFieldProps('TotalPriceWithTax')}
                        onChange={(value) =>
                          formik.setFieldValue('TotalPriceWithTax', Number(value))
                        }
                      />
                      {formik.touched.TotalPriceWithTax && formik.errors.TotalPriceWithTax && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.TotalPriceWithTax}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className='row'>
                <div className='col-lg-6'>
                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2'>
                      {intl.formatMessage({
                        id: 'REQUEST.GET.PURCHASEPROPOSALREQUESTITEMS.BRAND',
                      })}
                    </label>
                    <div className='col-lg-9 fv-row fv-plugins-icon-container'>
                      <input
                        type='text'
                        className='form-control form-control-solid mb-3 mb-lg-0'
                        autoComplete='off'
                        disabled={formik.isSubmitting}
                        {...formik.getFieldProps('Brand')}
                      />
                    </div>
                  </div>
                </div>
                <div className='col-lg-6'>
                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                      {intl.formatMessage({
                        id: 'REQUEST.GET.PURCHASEPROPOSALREQUESTITEMS.TSEFILE',
                      })}
                    </label>
                    <div className='col-lg-9 fv-row fv-plugins-icon-container'>
                      <label
                        htmlFor='TSEFile'
                        className='btn btn-outline btn-outline-dashed btn-outline-dark btn-active-light-dark position-relative overflow-hidden d-flex align-items-center justify-content-center'
                      >
                        <input
                          className='position-absolute z-index-2 opacity-0 w-1px h-1px'
                          type='file'
                          accept='image/*,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.kmz,.kml,.ncz,.dwg,.kmz,.kml,.ncz,.dwg'
                          id={'TSEFile'}
                          name={'TSEFile'}
                          onChange={(event) =>
                            formik.setFieldValue('TSEFile', event.currentTarget.files[0])
                          }
                        />
                        <KTSVG
                          path='/images/icons/duotune/communication/com008.svg'
                          className='svg-icon-3 position-relative'
                        />
                        {formik.values.TSEFile
                          ? formik.values.TSEFile.name
                          : intl.formatMessage({id: 'REQUEST.CREATE.FILE'})}
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className='row'>
                <div className='col-lg-6'>
                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2'>
                      {intl.formatMessage({
                        id: 'REQUEST.GET.PURCHASEPROPOSALREQUESTITEMS.TRANSPORTATION',
                      })}
                    </label>
                    <div className='col-lg-9 fv-row fv-plugins-icon-container'>
                      <input
                        type='text'
                        className='form-control form-control-solid mb-3 mb-lg-0'
                        autoComplete='off'
                        disabled={formik.isSubmitting}
                        {...formik.getFieldProps('Transportation')}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className='row pt-5'>
                <div className='col-lg-12 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                  <button
                    type='submit'
                    className='btn btn-primary'
                    data-kt-users-modal-action='submit'
                    disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                  >
                    <span className='indicator-label'>
                      {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                    </span>
                    {formik.isSubmitting && (
                      <span className='indicator-progress'>
                        {intl.formatMessage({id: 'PLEASE'})}
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </KTCardBody>

      <Modal
        className='modal-sticky modal-sticky-lg modal-sticky-bottom-right'
        id='kt_inbox_compose'
        role='dialog'
        data-backdrop='false'
        aria-hidden='true'
        tabIndex='-1'
        show={show}
        animation={false}
        aria-labelledby='contained-modal-title-vcenter'
        centered
        onHide={() => setShow(false)}
      >
        <div className='modal-content'>
          <div className='d-flex align-items-center justify-content-between py-5 ps-8 pe-5 border-bottom'>
            <h5 className='fw-bold m-0'>
              {intl.formatMessage({id: 'REQUEST.GET.PURCHASEPROPOSALREQUEST.MODAL.TITLE'})}
            </h5>
            <div className='d-flex ms-2'>
              <div
                className='btn btn-icon btn-sm btn-light-primary ms-2'
                data-bs-dismiss='modal'
                onClick={() => setShow(false)}
              >
                <KTSVG className='svg-icon-1' path='/images/icons/duotune/arrows/arr061.svg' />
              </div>
            </div>
          </div>

          <div className='d-block text-center py-5 ps-8 pe-5 '>
            <div className='d-block'>
              <KTSVG
                className='text-success'
                svgClassName='w-125px h-125px'
                path='/images/icons/duotune/files/fil008.svg'
              />
            </div>
            <div className='d-block text-center mt-6 mb-13'>
              <h1 className='mb-3'>
                {intl.formatMessage({id: 'REQUEST.GET.PURCHASEPROPOSALREQUEST.MODAL.HI'})}
              </h1>

              <p>
                {intl.formatMessage({id: 'REQUEST.GET.PURCHASEPROPOSALREQUEST.MODAL.DESCRIPTION'})}
              </p>
              <p>
                {intl.formatMessage({id: 'REQUEST.GET.PURCHASEPROPOSALREQUEST.MODAL.THANKYOU'})}
              </p>
            </div>
          </div>

          <div className='d-flex align-items-center justify-content-center py-5 ps-8 pe-5 border-top'>
            <div className='d-flex align-items-center me-3'>
              <button className='btn btn-light me-3' onClick={() => setShow(false)}>
                {intl.formatMessage({id: 'BUTTON.CLOSE'})}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {loading && <Loading />}
    </>
  )
}

export default PurchaseProposalRequest
